import type { RetailAccountContent } from '../types';
import { CompareIcon } from './CompareIcon';
import {
  formatPercent,
  formatSignedCurrency,
} from '../SharedAccountBody';

export interface PortfolioCompareDockItem {
  id: string;
  label: string;
  plAmount?: number;
  plPercent?: number;
}

export interface PortfolioCompareDockProps {
  items: PortfolioCompareDockItem[];
  onRemove: (id: string) => void;
  onClear: () => void;
  onCompare: () => void;
  compareDisabled?: boolean;
  compareLabel?: string;
}

const plColorClass = (value?: number) => {
  if (value === undefined) return '';
  if (value > 0) return 'text-positive';
  if (value < 0) return 'text-negative';
  return 'text-[rgba(255,255,255,0.55)]';
};

const PortfolioCompareDock = ({
  items,
  onRemove,
  onClear,
  onCompare,
  compareDisabled = false,
  compareLabel = 'Compare',
}: PortfolioCompareDockProps) => {
  if (items.length === 0) return null;

  const needMore = items.length < 2;

  return (
    <div
      className="pointer-events-none fixed bottom-[calc(20px+env(safe-area-inset-bottom,0px))] left-1/2 z-[200] w-[min(960px,calc(100%-32px))] -translate-x-1/2 max-[640px]:bottom-[calc(14px+env(safe-area-inset-bottom,0px))] max-[640px]:w-[calc(100%-20px)]"
      role="region"
      aria-label="Portfolio comparison"
    >
      <div className="pointer-events-auto flex flex-wrap items-end justify-between gap-4 rounded-[16px] border border-primary/22 bg-[rgba(12,16,28,0.94)] px-4 py-[14px] shadow-[0_12px_40px_rgba(0,0,0,0.48),0_4px_12px_rgba(0,0,0,0.28),inset_0_0_0_1px_rgba(255,255,255,0.04)] backdrop-blur-[16px] max-[640px]:px-[14px] max-[640px]:py-3">
        <div className="flex min-w-0 flex-[1_1_240px] flex-col gap-2.5">
          <div className="flex items-center gap-2.5">
            <span
              className="inline-flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px] border border-primary/35 bg-[linear-gradient(135deg,rgba(37,180,224,0.22)_0%,rgba(46,230,107,0.12)_100%)] text-[#8bd6f1]"
              aria-hidden
            >
              <CompareIcon size={18} />
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="bg-[linear-gradient(90deg,#25b4e0_0%,#5ed4f0_45%,#2ee66b_100%)] bg-clip-text text-[0.95rem] font-bold tracking-[0.01em] text-transparent">
                Portfolio Compare
              </span>
              <span className="text-[0.78rem] text-[rgba(255,255,255,0.52)]">
                {items.length} selected
                {needMore ? ' · pick at least 2' : ''}
              </span>
            </div>
          </div>
          <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0" aria-label="Selected portfolios">
            {items.map((item) => (
              <li
                key={item.id}
                className="inline-flex max-w-[min(100%,320px)] items-center gap-1.5 rounded-full border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.05)] py-1 pl-2.5 pr-1.5 text-[0.78rem] text-[rgba(255,255,255,0.88)]"
              >
                <span className="min-w-0 shrink truncate font-semibold">{item.label}</span>
                {item.plAmount !== undefined && (
                  <span
                    className={`flex-none whitespace-nowrap text-[0.72rem] font-semibold tabular-nums ${plColorClass(item.plAmount)}`}
                  >
                    {formatSignedCurrency(item.plAmount)}
                    {item.plPercent !== undefined && (
                      <span className="ml-[3px] opacity-90">
                        ({formatPercent(item.plPercent)})
                      </span>
                    )}
                  </span>
                )}
                <button
                  type="button"
                  className="inline-flex h-[18px] w-[18px] flex-none cursor-pointer items-center justify-center rounded-full border-none bg-[rgba(255,255,255,0.08)] p-0 text-[14px] leading-none text-[rgba(255,255,255,0.7)] hover:bg-negative/25 hover:text-negative"
                  onClick={() => onRemove(item.id)}
                  aria-label={`Remove ${item.label}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2 max-[640px]:w-full max-[640px]:justify-end">
          <button
            type="button"
            className="cursor-pointer rounded-[10px] border border-[rgba(255,255,255,0.18)] bg-transparent px-3 py-2 text-[0.85rem] font-medium text-[rgba(255,255,255,0.7)] hover:bg-[rgba(255,255,255,0.06)] hover:text-[rgba(255,255,255,0.92)]"
            onClick={onClear}
          >
            Clear
          </button>
          <button
            type="button"
            className="inline-flex cursor-pointer items-center gap-[7px] rounded-[10px] border border-primary/60 bg-[linear-gradient(135deg,#25b4e0_0%,#1e9fc8_100%)] px-4 py-2 text-[0.9rem] font-bold text-[#04111a] shadow-[0_4px_14px_rgba(37,180,224,0.28)] transition-[background,box-shadow,transform] duration-150 enabled:hover:-translate-y-px enabled:hover:bg-[linear-gradient(135deg,#4cc4ea_0%,#25b4e0_100%)] enabled:hover:shadow-[0_6px_18px_rgba(37,180,224,0.38)] disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none"
            onClick={onCompare}
            disabled={compareDisabled || needMore}
          >
            <CompareIcon size={15} />
            <span>{compareLabel}</span>
            {items.length >= 2 && (
              <span className="font-semibold opacity-85">({items.length})</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Extract since-start P/L for dock chips from loaded portfolio content. */
export const dockPlFromContent = (
  content?: RetailAccountContent,
): { plAmount?: number; plPercent?: number } => {
  const perf = content?.performance?.sinceStart;
  const plAmount = perf?.amount ?? content?.profitSinceStart;
  let plPercent = perf?.percent ?? content?.profitPercentage;
  if (plPercent !== undefined && (plPercent > 1 || plPercent < -1)) {
    plPercent /= 100;
  }
  return { plAmount, plPercent };
};

export default PortfolioCompareDock;
